import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  AttendeeSort,
  OrganizationAttendee,
  useOrganizationsApi,
} from 'services/api';
import type { DateFilterPreset } from 'hooks/useAnalyticsDateFilter';
import { PeoplePagination } from '../PeoplePagination';
import { AttendeeDetailBottomSheet } from './AttendeeDetailBottomSheet';
import { AttendeesDesktopTable } from './AttendeesDesktopTable';
import { AttendeesMobileCards } from './AttendeesMobileCards';
import { AttendeesToolbar } from './AttendeesToolbar';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface AttendeesSubTabProps {
  organizationId: string;
  organizationName?: string;
  dateFilterPreset: DateFilterPreset;
  dateFilterStartDate: Date | null;
  dateFilterEndDate: Date | null;
  onOpenDateFilter: () => void;
}

function escapeCsvCell (value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function AttendeesSubTab ({
  organizationId,
  organizationName,
  dateFilterPreset,
  dateFilterStartDate,
  dateFilterEndDate,
  onOpenDateFilter,
}: AttendeesSubTabProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();

  const [attendees, setAttendees] = useState<OrganizationAttendee[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<AttendeeSort>('engagement');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sheet, setSheet] = useState<OrganizationAttendee | null>(null);

  const startIso = useMemo(
    () => (dateFilterPreset === 'all-dates' ? undefined : dateFilterStartDate?.toISOString()),
    [dateFilterPreset, dateFilterStartDate],
  );
  const endIso = useMemo(
    () => (dateFilterPreset === 'all-dates' ? undefined : dateFilterEndDate?.toISOString()),
    [dateFilterPreset, dateFilterEndDate],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [sort, debouncedSearch, startIso, endIso]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await organizationsApi.listOrganizationAttendees(organizationId, {
        page,
        pageSize: PAGE_SIZE,
        sort,
        startDate: startIso,
        endDate: endIso,
        search: debouncedSearch || undefined,
      });
      if (cancelled) return;
      if (res.success) {
        setAttendees(res.value.attendees);
        setTotal(res.value.count);
      } else {
        enqueueSnackbar(res.error.message || 'Failed to load attendees', { variant: 'error' });
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, page, sort, debouncedSearch, startIso, endIso]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    setExporting(true);
    try {
      const allRows: OrganizationAttendee[] = [];
      let currentPage = 1;
      const exportPageSize = 200;
      while (currentPage <= totalPages) {
        const res = await organizationsApi.listOrganizationAttendees(organizationId, {
          page: currentPage,
          pageSize: exportPageSize,
          sort,
          startDate: startIso,
          endDate: endIso,
          search: debouncedSearch || undefined,
        });
        if (!res.success) {
          enqueueSnackbar(res.error.message || 'Failed to export attendees', { variant: 'error' });
          return;
        }
        allRows.push(...res.value.attendees);
        if (allRows.length >= res.value.count || res.value.attendees.length === 0) break;
        currentPage += 1;
      }

      const header = [
        'Name',
        'Email',
        'Role',
        'Last Attended',
        'RSVPs',
        'RSVP %',
        'Attended',
        'Attended %',
        'Engagement',
      ];
      const lines = [
        header.map(escapeCsvCell).join(','),
        ...allRows.map(a => {
          const rsvpPercent = a.totalEvents > 0
            ? Math.round((a.rsvpCount / a.totalEvents) * 100)
            : 0;
          return [
            `${a.firstName} ${a.lastName}`,
            a.schoolEmail ?? '',
            a.role ?? '',
            a.lastAttended ?? '',
            `${a.rsvpCount}/${a.totalEvents}`,
            `${rsvpPercent}%`,
            `${a.attendedCount}/${a.totalEvents}`,
            `${Math.round(a.attendedPercent)}%`,
            a.engagement,
          ]
            .map(v => escapeCsvCell(String(v)))
            .join(',');
        }),
      ];
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (organizationName || 'organization').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      a.href = url;
      a.download = `${slug}-attendees.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '24px', md: '16px' }, width: '100%' }}>
      <AttendeesToolbar
        sort={sort}
        onSortChange={setSort}
        search={search}
        onSearchChange={setSearch}
        dateFilterPreset={dateFilterPreset}
        dateFilterStartDate={dateFilterStartDate}
        dateFilterEndDate={dateFilterEndDate}
        onOpenDateFilter={onOpenDateFilter}
        onExport={handleExport}
        exporting={exporting}
      />

      {loading && attendees.length === 0 ? (
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: '48px' }}>
          <CircularProgress size={28} />
        </Box>
      ) : !attendees.length ? (
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          No attendees match your filters.
        </Typography>
      ) : isMobile ? (
        <AttendeesMobileCards attendees={attendees} />
      ) : (
        <AttendeesDesktopTable attendees={attendees} />
      )}

      {attendees.length > 0 ? (
        <PeoplePagination
          page={page}
          totalPages={totalPages}
          onPrev={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      ) : null}

      <AttendeeDetailBottomSheet
        open={!!sheet}
        attendee={sheet}
        onClose={() => setSheet(null)}
      />
    </Box>
  );
}
